import type { CancellationPolicy } from './queries';
import {
  cancellationServicePolicy,
  CancellationServicePolicy,
  cancellationConditions,
  CancellationCondition,
} from '@bandido/shared/types';

/**
 * La política de cancelación, resuelta para mostrarla ANTES del botón rojo.
 *
 * Cancelar es irreversible y cuesta plata: qué se cobra y qué se devuelve
 * tiene que estar en pantalla cuando el asesor decide, no después. El
 * contrato del hub lo publica de dos formas complementarias y puede mandar
 * una, las dos o ninguna — `current` (qué costaría cancelar HOY, el enquiry
 * puntual del proveedor) y `schedule` (el calendario por rangos de fecha) —,
 * así que de acá sale UNA sola expresión con todo lo que el diálogo dibuja y
 * con el permiso para ejecutar: el CTA se habilita si y solo si algo de lo
 * que hay acá está en pantalla (los números, o el aviso honesto de que el
 * proveedor no los publica). Nunca se cancela a ciegas.
 *
 * Nada se calcula: los importes llegan resueltos del backend y acá solo se
 * eligen y se formatean (`formatMoney`). Lo único que se hace con un número
 * es COMPARARLO contra cero, para decidir qué frase corresponde; ningún
 * importe válido del contrato se esconde.
 */
export type CancellationPolicyStatus =
  /** Todavía se está preguntando: no hay nada que afirmar sobre la plata. */
  | 'loading'
  /** El proveedor publicó algo: números, calendario, o su mensaje. */
  | 'published'
  /** No hay política: se cancela igual, pero diciéndolo. */
  | 'unpublished';

/**
 * Qué frase describe el costo de cancelar HOY. La elige lo que el proveedor
 * publicó, no el componente: así no puede quedar una frase que promete un
 * reintegro que no vino, ni un "sin costo" sobre un cargo que sí existe.
 */
export type CancellationCostKey =
  /** Cuesta y devuelve. */
  | 'costWithRefund'
  /** Cuesta, y no dijo cuánto devuelve. */
  | 'cost'
  /** No cuesta nada, y devuelve. */
  | 'freeWithRefund'
  /** No cuesta nada, y no dijo cuánto devuelve. */
  | 'free'
  /** Solo dijo cuánto devuelve. */
  | 'refund';

export interface CancellationCost {
  key: CancellationCostKey;
  feeMinor?: number;
  refundMinor?: number;
  /**
   * Cancelar hoy saca plata (hay cargo, o no se devuelve nada). Decide el
   * tono del callout: el rojo suave es para cuando cuesta, no para toda
   * cancelación — una ventana gratuita en rojo enseña a ignorar el rojo.
   */
  costly: boolean;
}

export interface NoShowCost {
  feeMinor?: number;
  fullVoucherValue: boolean;
}

export interface CancellationScheduleRow {
  /** Tal como los publicó el proveedor: acá no se reescriben. */
  from?: string;
  to?: string;
  feePercent?: number;
  managementFeeMinor?: number;
}

export interface CancellationPolicyView {
  conditionalCharges?: CancellationCondition[];
  servicePolicy?: CancellationServicePolicy;
  penaltyUnknown?: boolean;
  refundUnknown?: boolean;
  status: CancellationPolicyStatus;
  /** La moneda de TODOS los importes de la política (contrato). */
  currency?: string;
  /** El costo de hoy, en grande. `null` si el proveedor no lo publicó. */
  cost: CancellationCost | null;
  /** El proveedor retiene el valor completo del voucher al cancelar hoy. */
  fullVoucherValue: boolean;
  /** Cargo específico de no-show, separado del costo de cancelar hoy. */
  noShow: NoShowCost | null;
  /** El texto del proveedor, tal cual: es el que explica su política. */
  message?: string;
  /** El calendario por rangos, con la fila vigente marcada. */
  schedule: CancellationScheduleRow[];
  /**
   * El CTA rojo puede ejecutar. Se DERIVA de que haya algo dicho sobre la
   * plata en pantalla —los números publicados o el aviso de que no los
   * publican—, así que no puede habilitarse mientras el diálogo está vacío
   * esperando la respuesta del proveedor.
   */
  canCancel: boolean;
  /**
   * Lo que el CTA acepta: un cargo. Solo se afirma lo contrario cuando el
   * proveedor publicó explícitamente que cancelar hoy no cuesta nada —
   * cualquier otra cosa, incluido "no publica", puede tener costo, y un botón
   * que promete gratis lo que puede cobrarse es peor que no decir nada.
   */
  acceptsCharge: boolean;
}

const ISO_CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

const declared = (value?: string): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const finite = (value?: number): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/**
 * Un importe en unidad mínima, tal como lo define el contrato: entero y no
 * negativo. Lo que no lo es no es plata —un `NaN`, un float, un negativo— y
 * no se dibuja: pintar "UYU 0,95" por un `94.6` que el hub no debería mandar
 * es afirmar un precio que nadie publicó.
 */
const minorAmount = (value?: number): number | undefined => {
  const amount = finite(value);
  return amount !== undefined && Number.isInteger(amount) && amount >= 0 ? amount : undefined;
};

/**
 * La moneda, como la declara el contrato: ISO 4217 de 3 letras. Sin una
 * moneda legible no hay importe que se pueda decir (ver `resolveCost`).
 */
const currencyOf = (value?: string): string | undefined =>
  typeof value === 'string' && /^[A-Z]{3}$/.test(value) ? value : undefined;

/**
 * La fecha de calendario de un extremo del rango, `YYYY-MM-DD`, comparable
 * como texto (los campos vienen con ceros a la izquierda o no se aceptan).
 *
 * Valida el calendario para presentar fechas, sin convertir zonas horarias.
 * No sirve para determinar la vigencia de una penalidad: sus cortes incluyen
 * horas locales y `CancellationPolicy` no declara una zona horaria.
 */
export function calendarDateKey(value?: string): string | null {
  const raw = declared(value);
  if (!raw) return null;
  const match = ISO_CALENDAR_DATE.exec(raw);
  if (!match) return null;
  const [, year, month, day] = match;
  const probe = new Date(Date.UTC(2000, Number(month) - 1, Number(day)));
  if (probe.getUTCMonth() !== Number(month) - 1 || probe.getUTCDate() !== Number(day)) {
    return null;
  }
  // 29 de febrero solo existe en año bisiesto: el sondeo va sobre el año real.
  if (Number(month) === 2 && Number(day) === 29) {
    const leap = new Date(Date.UTC(2000, 1, 29));
    leap.setUTCFullYear(Number(year));
    if (leap.getUTCDate() !== 29) return null;
  }
  return `${year}-${month}-${day}`;
}

/** El día del asesor, en la misma forma comparable que los rangos. */
export function todayKey(now: Date): string {
  const year = String(now.getFullYear()).padStart(4, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// El calendario no declara zona horaria ni una regla vigente autoritativa.
// No se compara con el reloj del asesor: el costo actual viene de `current`.

/**
 * El costo de hoy, tal como lo publicó el proveedor.
 *
 * ⚠️ Acá NO se descarta ningún importe por ser "demasiado grande". El
 * contrato define `feeMinor` como el cargo por cancelar hoy y no declara
 * centinelas ni cotas, así que esconder un cargo alto —comparándolo, además,
 * contra el precio de VENTA de la fila, que lleva markup y puede estar en
 * otra moneda— es tapar plata real justo antes de una acción irreversible. Si
 * un proveedor codifica un "no-show" con un número enorme, eso se normaliza
 * en SU adapter (del lado del hub), no se adivina en la pantalla.
 */
function resolveCost(current: CancellationPolicy['current'], hasCurrency: boolean): CancellationCost | null {
  // Sin moneda no hay importe que se pueda decir: el contrato pone los
  // importes en la unidad mínima de `currency`, y un número sin ella no es
  // plata. Queda el mensaje del proveedor, que no depende de eso.
  if (!current || !hasCurrency) return null;

  const feeMinor = minorAmount(current.feeMinor);
  const refundMinor = minorAmount(current.refundMinor);
  const costly = (feeMinor !== undefined && feeMinor > 0) || refundMinor === 0;

  if (feeMinor !== undefined && feeMinor > 0) {
    return refundMinor !== undefined
      ? { key: 'costWithRefund', feeMinor, refundMinor, costly }
      : { key: 'cost', feeMinor, costly };
  }
  if (feeMinor === 0) {
    return refundMinor !== undefined
      ? { key: 'freeWithRefund', feeMinor, refundMinor, costly }
      : { key: 'free', feeMinor, costly };
  }
  return refundMinor !== undefined ? { key: 'refund', refundMinor, costly } : null;
}

function resolveNoShow(
  noShow: NonNullable<NonNullable<CancellationPolicy['current']>['noShow']> | undefined,
  hasCurrency: boolean,
): NoShowCost | null {
  if (!noShow || typeof noShow !== 'object') return null;
  const fullVoucherValue = noShow.fullVoucherValue === true;
  const feeMinor = hasCurrency ? minorAmount(noShow.feeMinor) : undefined;
  return fullVoucherValue || feeMinor !== undefined ? { feeMinor, fullVoucherValue } : null;
}

/**
 * Las filas del calendario que dicen algo. Una fila sin fechas, sin
 * porcentaje y sin gastos no es una regla: dibujarla es una fila vacía que
 * hace pasar por "publicada" una política que no lo está.
 */
function resolveSchedule(schedule: CancellationPolicy['schedule']): CancellationScheduleRow[] {
  const rows: CancellationScheduleRow[] = [];

  for (const rule of Array.isArray(schedule) ? schedule : []) {
    const from = declared(rule?.from);
    const to = declared(rule?.to);
    const feePercent = finite(rule?.feePercent);
    const managementFeeMinor = minorAmount(rule?.managementFeeMinor);
    if (!from && !to && feePercent === undefined && managementFeeMinor === undefined) {
      continue;
    }
    rows.push({ from, to, feePercent, managementFeeMinor });
  }

  return rows;
}

export interface ResolveCancellationPolicyInput {
  /** Lo que contestó el proveedor. `null` mientras se pide o si no hay. */
  policy: CancellationPolicy | null;
  /** El enquiry está en vuelo. */
  isLoading: boolean;
}

export function resolveCancellationPolicy({
  policy,
  isLoading,
}: ResolveCancellationPolicyInput): CancellationPolicyView {
  const currency = currencyOf(policy?.currency);
  const determinationUnknown = policy?.current?.determination === 'unknown';
  const conditionalCharges = cancellationConditions(policy?.conditionalCharges) ?? [];
  const penaltyUnknown = determinationUnknown || policy?.economics?.penaltyStatus === 'unknown';
  const refundUnknown = determinationUnknown || policy?.economics?.refundStatus === 'unknown';
  const cost = resolveCost(
    policy?.current
      ? {
        ...policy.current,
        ...(penaltyUnknown ? { feeMinor: undefined } : {}),
        ...(refundUnknown ? { refundMinor: undefined } : {}),
      }
      : undefined,
    Boolean(currency),
  );
  const fullVoucherValue = !penaltyUnknown && policy?.current?.fullVoucherValue === true;
  const noShow = conditionalCharges.some(
    (rule) => rule.event === 'no_show' && (rule.charge.feeMinor !== undefined || rule.charge.fullVoucherValue),
  )
    ? null
    : resolveNoShow(policy?.current?.noShow, Boolean(currency));
  const message = declared(policy?.current?.message);
  const schedule = resolveSchedule(policy?.schedule);
  const parsed = cancellationServicePolicy(policy);
  const servicePolicy =
    parsed && (parsed.scopeStatus || parsed.services || parsed.rulesSummary || parsed.unattributedRules)
      ? parsed
      : undefined;

  // El estado sale de lo que se va a DIBUJAR, no de si la respuesta llegó:
  // una respuesta vacía y una capacidad que dice que no publican son la misma
  // cosa para el asesor, y las dos merecen el aviso honesto.
  const status: CancellationPolicyStatus = isLoading
    ? 'loading'
    : cost ||
        fullVoucherValue ||
        noShow ||
        message ||
        schedule.length > 0 ||
        conditionalCharges.length > 0 ||
        servicePolicy ||
        penaltyUnknown ||
        refundUnknown
      ? 'published'
      : 'unpublished';

  return {
    ...(conditionalCharges.length ? { conditionalCharges } : {}),
    ...(servicePolicy ? { servicePolicy } : {}),
    ...(penaltyUnknown ? { penaltyUnknown } : {}),
    ...(refundUnknown ? { refundUnknown } : {}),
    status,
    currency,
    cost,
    fullVoucherValue,
    noShow,
    message,
    schedule,
    // La MISMA condición que dibuja algo sobre la plata habilita el botón:
    // con el diálogo esperando la respuesta no hay nada que aceptar.
    canCancel: status !== 'loading',
    acceptsCharge: fullVoucherValue || !(cost && cost.feeMinor === 0 && !cost.costly),
  };
}
